import {
  AnalyticsActionLoginEvent,
  AnalyticsActionLogoutEvent,
  AnalyticsEventParam,
} from '@accedo/one-plugin-sdk';
import { LS } from 'ls-ts';
import { v4 as uuidv4 } from 'uuid';
import { proxy, useSnapshot } from 'valtio';

import {
  AuthenticationType,
  Entitlements,
  Profile,
  ProfileResponse,
  TokenResponse,
  userService,
} from '#/client';
import devtoolsApi from '#/dev/devtools';
import { CoreAuthFeature } from '#/service/config';
import { StorageKeys } from '#/service/constants';
import { sleep } from '#/service/utils';

import { WrappedStore } from '../common';
import {
  canManageSubscription,
  getAnonymousToken,
  saveAuthToken,
} from './utils';

export interface AuthState {
  /* is standard or TVE login enabled based on core authentication config */
  isAuthEnabled: boolean;
  isInitialised: boolean;
  /* is user logged in */
  isLoggedIn: boolean;
  /* user has subscription or not */
  isEntitled: boolean;
  /* expiration date of subscription */
  expirationDate: string;
  type: AuthenticationType;
  /**
   * It's effectively the same as `isWeb` flag in subscription details, but this is
   * extracted from `/profile` response data. It determines if user can manage subscription
   * on Web platform
   * */
  canManageSubscription: boolean;
  userId?: string;
  sessionId?: string;
  profile: Profile;
  entitlements: Entitlements;
  /* authentication config from core */
  config?: CoreAuthFeature;
  /* is login modal open */
  isLoginShown: boolean;
  purchaseEntitlements?: string[];
  /* is standard login type enabled in config */
  isStandardLogin: boolean;
  /* is standard login related forgot password feature enabled in core authentication config */
  isForgotPasswordEnabled: boolean;
  /* is standard login related registration feature enabled in core authentication config */
  isRegistrationEnabled: boolean;
  /* is second screen login feature enabled in core authentication config */
  isSecondScreen: boolean;
  /* is TVE login feature enabled in core authentication config */
  isTve: boolean;
  /** Used only after registration succeeded to track if we have entitlements ready */
  awaitingEntitlements?: boolean;
  /** Track if the user was logged in since app start */
  wasLoggedIn?: boolean;
}

export const defaultAuthState: AuthState = {
  isAuthEnabled: false,
  isInitialised: false,
  isLoggedIn: false,
  canManageSubscription: false,
  isEntitled: false,
  isLoginShown: false,
  expirationDate: '',
  type: AuthenticationType.None,
  entitlements: {
    subscriber: false,
    grantedEntitlementTags: [],
  },
  profile: {
    attributes: {},
  },
  isStandardLogin: false,
  isForgotPasswordEnabled: false,
  isRegistrationEnabled: false,
  isSecondScreen: false,
  isTve: false,
  wasLoggedIn: false,
};

const _authStore = proxy<WrappedStore<AuthState>>({
  value: defaultAuthState,
});

/**
 * Object representing Auth state.
 * Please DO NOT use it directly in the app's code (tests and other stores are exception here).
 * Instead, you should use:
 * @example const { ... } = useAuthentication();
 **/
export const authStore = _authStore.value;

/**
 * Function setting useful flags from authentication core config to auth store.
 *
 * @config: core authentication config
 **/
const setAuthenticationFeatures = (config: CoreAuthFeature) => {
  authStore.isAuthEnabled =
    !!config?.standard?.enabled || !!config?.tve?.enabled;
  authStore.isStandardLogin = !!config?.standard?.enabled;
  authStore.isForgotPasswordEnabled =
    !!config?.standard?.forgotPassword?.enabled;
  authStore.isRegistrationEnabled = !!config?.standard?.registration?.enabled;
  authStore.isTve = !!config?.tve?.enabled;
  authStore.isSecondScreen = !!config?.secondScreen?.enabled;
};

// 3.1 seconds, because MPP have up to 15s timeout. I saw in the testing wait times
// of 6s or 8s in the most scenarios, so 3.1s is a safe bet (0.1s for safety to not
// land in another window)
const RETRY_AUTHPROFILE_TIMEOUT_MS = 3100;
export const MAX_AUTHPROFILE_RETRIES = 5;

export const authActions = {
  updateProfile(profile: ProfileResponse) {
    authStore.type = profile.authentication.type;
    authStore.userId = profile.userId;
    authStore.profile = profile.profile;
    authStore.entitlements = profile.entitlements;

    if (!authStore.sessionId) {
      authStore.sessionId = uuidv4();
    }

    authStore.entitlements.grantedEntitlementTags ||= [];

    authStore.isLoggedIn = !!authStore.userId;
    authStore.isEntitled = authStore.entitlements.subscriber || false;
    authStore.expirationDate = profile.authentication.expiration;
    authStore.canManageSubscription = canManageSubscription(profile);
  },

  setProfileAttribute(fieldName: string, fieldValue: any) {
    authStore.profile.attributes[fieldName] = fieldValue;
  },

  setProfilePreferences(fieldName: string, fieldValue: any) {
    authStore.profile.preferences ||= {};
    authStore.profile.preferences[fieldName] = fieldValue;
  },

  /** Show login modal */
  showLogin() {
    authStore.isLoginShown = true;
  },

  /** Hide login modal */
  hideLogin() {
    authStore.isLoginShown = false;
  },

  // TODO#configStore: auth config should be moved to a separate configStore
  setConfig(config: CoreAuthFeature) {
    authStore.config = config;

    setAuthenticationFeatures(config);
  },

  setPurchaseEntitlements(entitlements?: string[]) {
    authStore.purchaseEntitlements = entitlements;
  },

  /** Logs user out by executing all necessary things like:
   * - updating store
   * - firing necessary analytics events
   * - removing saved data from local storage
   */
  async logout() {
    document.dispatchEvent(
      new AnalyticsActionLogoutEvent({
        [AnalyticsEventParam.UserId]: authStore.userId,
        [AnalyticsEventParam.SessionId]: authStore.sessionId ?? '',
      })
    );
    await getAnonymousToken();

    logout();
    LS.remove(StorageKeys.AUTHENTICATION);
  },

  /**
   * When you authenticated user correctly use this method to update
   * store accordingly and kick off necessary analytic events
   **/
  async login(tokenResponse: TokenResponse) {
    const profile = await authActions.refreshProfile();
    saveAuthToken(tokenResponse);
    authStore.wasLoggedIn = true;

    document.dispatchEvent(
      new AnalyticsActionLoginEvent({
        [AnalyticsEventParam.UserId]: authStore.userId,
        [AnalyticsEventParam.SessionId]: authStore.sessionId,
      })
    );
    authActions.hideLogin();

    return profile;
  },

  async refreshProfile() {
    try {
      const profileRes = await userService.getProfile();
      const profile = profileRes.data;

      authActions.updateProfile(profile);

      return {
        userId: authStore.userId,
        sessionId: authStore.sessionId,
        errors: profile._meta.errors,
        subscribed: profile.subscriber ?? false,
      };
    } catch (err) {
      return {
        userId: authStore.userId,
        sessionId: authStore.sessionId,
        subscribed: false,
      };
    }
  },

  /**
   * This is pretty much the same as refreshProfile action.
   * The difference is that this method is meant to be used after successful purchase of a plan.
   * We and user expects that after plan purchase he can watch content immediately.
   * It will run the standard refreshProfile action and in case entitlements are still not granted,
   * it will re-try X times. Reason is that it may take for the backend up to 15s to process the purchase.
   **/
  async refreshProfileAfterEntitlementPurchase() {
    authStore.awaitingEntitlements = true;
    let result = await authActions.refreshProfile();

    if (result.subscribed) {
      authStore.awaitingEntitlements = false;

      return result;
    }

    for (let i = 0; i < MAX_AUTHPROFILE_RETRIES; i++) {
      await sleep(RETRY_AUTHPROFILE_TIMEOUT_MS);

      result = await authActions.refreshProfile();

      if (result.subscribed) {
        authStore.awaitingEntitlements = false;

        return result;
      }
    }

    authStore.awaitingEntitlements = false;
    console.warn(
      `Entitlements were not granted after ${MAX_AUTHPROFILE_RETRIES} retries.`
    );

    return result;
  },

  /** A way to reset auth store to the initial state. It is only meant to be used in tests! */
  reset() {
    _authStore.value = defaultAuthState;
  },
};

export const useAuthentication = () => useSnapshot(authStore) as AuthState;

export async function initAuth() {
  // The authentication state is maintained via cookies:
  // to read it, send a request to get the user profile and determine if any was provided
  try {
    await authActions.refreshProfile();
  } catch (err) {
    // if the profile request fails, we will get an anonymous token instead.
  }

  if (authStore.type === AuthenticationType.None) {
    await getAnonymousToken();
  }

  // now we have a token, either authenticated or anonymous.
  authStore.isInitialised = true;
}

function logout() {
  authStore.isLoggedIn = false;
  authStore.isEntitled = false;
  authStore.expirationDate = '';

  authStore.type = AuthenticationType.None;
  authStore.userId = undefined;
  authStore.sessionId = undefined;
  authStore.profile = {
    attributes: {},
    preferences: {},
  };
  authStore.entitlements = {
    grantedEntitlementTags: [],
    subscriber: false,
  };
}

devtoolsApi.setStore('auth', authStore, authActions);
